import { useAuth } from "@/src/contexts/auth.context";
import { useReactToPost } from "@/src/hooks/features/posts/use-react-to-post";
import { useShareWithPoints } from "@/src/hooks/features/posts/use-share-with-points";
import {
  INLINE_MENTION_TRIGGER,
  appendNewMentions,
  stripInlineMentionTrigger,
} from "@/src/hooks/features/posts/use-post-creation-modal";
import { Post } from "@/src/models/post.model";
import { SelectableItem } from "@/src/components/action-modals/item-selection/item-row";
import { useSendNotification } from "@/src/services/api/notification/notification.hooks";
import {
  useCreateCommentMutation,
  useDeleteCommentMutation,
  useGetCommentsQuery,
  useGetPostByIdQuery,
  useUpdatePostScoreMutation,
} from "@/src/services/api/posts/posts.hooks";
import { hot } from "@/src/utils/feed-score";
import { splitAttachmentUrls } from "@/src/utils/attachment.utils";
import { useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { TextInput } from "react-native";
import { useToggleSavePost } from "./use-toggle-save-post";

type UsePostDetailOptions = Readonly<{
  postId: string;
  visible: boolean;
}>;

export const usePostDetail = ({ postId, visible }: UsePostDetailOptions) => {
  const { user } = useAuth();
  const { t } = useTranslation();

  const { handleSharedSuccessfully } = useShareWithPoints();

  const [commentText, setCommentText] = useState("");
  const [commentMentionedUsers, setCommentMentionedUsers] = useState<SelectableItem[]>([]);
  const [showCommentMentionModal, setShowCommentMentionModal] = useState(false);
  const [shareModalVisible, setShareModalVisible] = useState(false);
  const [replyTarget, setReplyTarget] = useState<{
    commentId: string;
    author: string;
  } | null>(null);

  const suppressedCommentMentionContent = useRef<string | null>(null);

  const inputRef = useRef<TextInput>(null);
  const {
    data: activePost,
    isLoading: isPostLoading,
    isError: isPostError,
    isSuccess: isPostLoaded,
  } = useGetPostByIdQuery(postId, { enabled: visible && !!postId });

  /**
   * The post was looked up and isn't there — deleted, or an id that never
   * existed.
   *
   * This needs to be its own state rather than being lumped in with "loading".
   * `useGetPostByIdQuery` resolves to `null` for a missing post, which is a
   * *successful* query, so the detail modal's `isPostLoading || !activePost`
   * check left the skeleton up forever. Notifications outlive the post they
   * point at, so tapping "X commented on your post" after the post was deleted
   * was a permanent spinner.
   */
  const isPostMissing = isPostLoaded && !activePost;
  const { isSaved, handleToggleSave } = useToggleSavePost(activePost ?? null);

  const { userReaction, handleReact, reactionCount } = useReactToPost(
    activePost ?? ({} as Post),
  );

  const { mediaUrls, fileUrls } = splitAttachmentUrls(
    activePost?.attachmentUrls,
  );

  const {
    data: commentsData,
    isLoading: isCommentsLoading,
    isError: isCommentsError,
    refetch: refetchComments,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useGetCommentsQuery(activePost?.ItemId ?? "", undefined);

  const comments =
    commentsData?.pages.flatMap((page) => page.getcomments.items) ?? [];

  const { mutate: createComment, isPending: isCommentPending } =
    useCreateCommentMutation();

  const { mutate: deleteComment } = useDeleteCommentMutation();

  const { mutate: updateScore } = useUpdatePostScoreMutation();

  const { mutate: sendNotification } = useSendNotification();

  const isCommentDisabled = !commentText.trim();

  const handleCommentTextChange = useCallback((text: string) => {
    setCommentText(text);
    if (suppressedCommentMentionContent.current !== null && !text.endsWith('@')) {
      suppressedCommentMentionContent.current = null;
    }
    if (
      INLINE_MENTION_TRIGGER.test(text) &&
      text !== suppressedCommentMentionContent.current
    ) {
      suppressedCommentMentionContent.current = text;
      setShowCommentMentionModal(true);
    }
  }, []);

  /**
   * Always appends, for the same reason as the post composer: with the toolbar
   * mention button gone the inline @ is the only way in, and the picker opens
   * with nothing preselected, so replacing would drop the pills already added.
   *
   * Also clears the '@' that opened the picker — leaving it behind read as an
   * unfinished mention and re-armed the trigger on the next keystroke.
   */
  const handleCommentMentionConfirm = useCallback((items: SelectableItem[]) => {
    setCommentMentionedUsers((prev) => appendNewMentions(prev, items));
    setCommentText(stripInlineMentionTrigger);
    suppressedCommentMentionContent.current = null;
    setShowCommentMentionModal(false);
  }, []);

  const handleCommentMentionCancel = useCallback(() => {
    setShowCommentMentionModal(false);
  }, []);

  const handlePostComment = () => {
    if (!commentText.trim() || !activePost) return;

    const parentComment = replyTarget
      ? comments.find((comment) => comment.ItemId === replyTarget.commentId)
      : null;

    const snapshotMentioned = [...commentMentionedUsers];
    const trimmedText = commentText.trim();
    const draft = { text: commentText, replyTarget, mentioned: snapshotMentioned };

    // The comment shows at once (optimistically, see useCreateCommentMutation),
    // so the box clears at once too; a failed send puts the draft back.
    setCommentText("");
    setReplyTarget(null);
    setCommentMentionedUsers([]);

    createComment(
      {
        postId: activePost.ItemId,
        content: trimmedText,
        parentCommentId: replyTarget?.commentId,
        parentReplyCount: parentComment?.replyCount,
        postCommentCount: activePost.commentCount,
        authorId: user?.itemId ?? "",
        authorSnapshot: {
          firstName: user?.firstName,
          lastName: user?.lastName,
          profileImageUrl: user?.profileImageUrl ?? "",
        },
        replyCount: 0,
        mentionedUsers: snapshotMentioned,
      },
      {
        onError: () => {
          setCommentText((current) => (current ? current : draft.text));
          setReplyTarget((current) => current ?? draft.replyTarget);
          setCommentMentionedUsers((current) =>
            current.length ? current : draft.mentioned,
          );
        },
        onSuccess: (data: any) => {

          const totalReacts = Object.values(activePost.reactionCount ?? {}).reduce(
            (a, b) => a + b,
            0,
          );
          updateScore({
            id: activePost.ItemId,
            score: hot(totalReacts, (activePost.commentCount ?? 0) + 1, new Date(activePost.CreatedDate)),
          });

          const postAuthorId = activePost.authorSnapshot?.userId;

          if (postAuthorId !== user?.itemId) {
            sendNotification({
              userIds: [postAuthorId],
              denormalizedPayload: JSON.stringify({
                type: "post_comment",
                message: t("LABEL_NOTIFY_POST_COMMENT", {
                  firstName: user?.firstName,
                  lastName: user?.lastName,
                  preview: `${trimmedText.slice(0, 80)}${trimmedText.length > 80 ? "…" : ""}`,
                }),
                actorAvatar: user?.profileImageUrl,
                targetEntityId: activePost.ItemId,
              }),
              configurationName: "post-comment",
              responseKey: "post-action",
              responseValue: "comment",
            });
          }

          // Mention notifications: skip self, skip post author (already notified above)
          const currentUserId = user?.itemId;
          const uniqueMentionIds = [
            ...new Set(snapshotMentioned.map((u) => u.itemId)),
          ]
            .filter((id) => id !== currentUserId)
            .filter((id) => id !== postAuthorId);

          for (const recipientId of uniqueMentionIds) {
            sendNotification({
              userIds: [recipientId],
              denormalizedPayload: JSON.stringify({
                // See the note in use-posts: the renderer switches on "mention".
                type: "mention",
                message: t("LABEL_NOTIFY_COMMENT_MENTION", {
                  firstName: user?.firstName,
                  lastName: user?.lastName,
                }),
                postId: activePost.ItemId,
                commentId: data?.insertcomment?.itemId,
                actorId: user?.itemId ?? "",
                actorName: [user?.firstName, user?.lastName]
                  .filter(Boolean)
                  .join(" "),
                actorAvatar: user?.profileImageUrl,
                targetEntityId: activePost.ItemId,
              }),
              configurationName: "mention",
              responseKey: "mention-action",
              responseValue: "comment",
            });
          }
        },
      },
    );
  };

  const handleDeleteComment = (commentId: string) => {
    if (!activePost) return;
    const totalReacts = Object.values(activePost.reactionCount ?? {}).reduce(
      (a, b) => a + b,
      0,
    );
    deleteComment(
      {
        commentId,
        postId: activePost.ItemId,
        postCommentCount: activePost.commentCount ?? 0,
      },
      {
        onSuccess: () => {
          updateScore({
            id: activePost.ItemId,
            score: hot(
              totalReacts,
              Math.max((activePost.commentCount ?? 1) - 1, 0),
              new Date(activePost.CreatedDate),
            ),
          });
        },
      },
    );
  };

  const handleReply = (commentId: string, author: string) => {
    setReplyTarget({ commentId, author });
    inputRef.current?.focus();
  };

  const handleCancelReply = () => setReplyTarget(null);

  const handleShareNotification = () => {
    if (!activePost || activePost.authorSnapshot?.userId === user?.itemId)
      return;
    sendNotification({
      userIds: [activePost.authorSnapshot.userId],
      denormalizedPayload: JSON.stringify({
        type: "post_share",
        message: t("LABEL_NOTIFY_POST_SHARE", {
          firstName: user?.firstName,
          lastName: user?.lastName,
        }),
        actorAvatar: user?.profileImageUrl,
        targetEntityId: activePost.ItemId,
      }),
      configurationName: "post-share",
      responseKey: "post-action",
      responseValue: "share",
    });
  };
  const handleShareSuccess = () => {
    setShareModalVisible(false);
    handleSharedSuccessfully(activePost?.ItemId);
  };

  return {
    user,
    // post
    activePost,
    isPostLoading,
    isPostError,
    isPostMissing,
    mediaUrls,
    fileUrls,
    // reactions
    userReaction,
    handleReact,
    reactionCount,
    // comments
    comments,
    isCommentsLoading,
    isCommentsError,
    refetchComments,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    // comment input
    commentText,
    setCommentText,
    handleCommentTextChange,
    isCommentDisabled,
    isCommentPending,
    handlePostComment,
    handleDeleteComment,
    inputRef,
    // comment mentions
    commentMentionedUsers,
    setCommentMentionedUsers,
    showCommentMentionModal,
    handleCommentMentionConfirm,
    handleCommentMentionCancel,
    // reply
    replyTarget,
    handleReply,
    handleCancelReply,
    // share
    shareModalVisible,
    setShareModalVisible,
    handleShareNotification,
    handleShareSuccess,

    // Save
    isSaved,
    handleToggleSave,
  };
};
